import React, { useEffect, useRef, useState } from "react";
import { Product, ProductId } from "./product";
import { Icon, Button } from "../components/tailwind";
import {
  getProduct,
  updateProductParent,
  fetchParentFromInriver,
} from "../api/action";
import { useSelector } from "react-redux";
import { RootState, store } from "../utils/store";
import {
  setDjangoToastOpen,
  NotificationAppearance,
} from "../api/djangoToastSlice";

type Props = {
  customerSlug: string;
  productId: ProductId;
  inriverId?: string;
};

export const ProductDetailEditParentButton: React.FC<Props> = ({
  customerSlug,
  productId,
  inriverId,
}) => {
  // Store the external id of the parent product
  const [currentParentEntity, setCurrentParentEntity] = useState("");
  // Store the value entered by the user
  const [parentEntity, setParentEntity] = useState("");
  // Current product
  const [product, setProduct] = useState<Product | null>(null);
  const [isFetchingParent, setIsFetchingParent] = useState(false);
  const mounted = useRef(true);
  const token = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    (async (): Promise<void> => {
      mounted.current = true;
      const product = await getProduct(token, productId);
      if (!mounted.current) {
        return;
      }
      const entity = product?.parent_external_id || product?.parent_id || "";
      setCurrentParentEntity(entity);
      setParentEntity(entity);
      setProduct(product);
    })();
    return (): void => {
      mounted.current = false;
    };
  }, [productId, token]);

  const trimmedInput = parentEntity.trim();
  const hasNoParentLinked = !currentParentEntity;
  const isFetchParentMode = Boolean(
    inriverId && product && hasNoParentLinked && trimmedInput === ""
  );

  const syncParentFieldsFromProduct = (product: Product | null): void => {
    const entity = product?.parent_external_id || product?.parent_id || "";
    setCurrentParentEntity(entity);
    setParentEntity(entity);
    setProduct(product);
  };

  const onFetchParentFromInriver = async (): Promise<void> => {
    if (!product || !mounted.current) {
      return;
    }
    setIsFetchingParent(true);
    try {
      const response = await fetchParentFromInriver({
        productId: product.id,
        token,
      });
      const fresh = await getProduct(token, productId);
      if (!mounted.current) {
        return;
      }
      syncParentFieldsFromProduct(fresh);
      store.dispatch(
        setDjangoToastOpen({
          appearance: response.result
            ? NotificationAppearance.SUCCESS
            : NotificationAppearance.ERROR,
          content: response.message,
        })
      );
    } catch {
      if (mounted.current) {
        store.dispatch(
          setDjangoToastOpen({
            appearance: NotificationAppearance.ERROR,
            content: "Could not fetch parent from InRiver",
          })
        );
      }
    } finally {
      if (mounted.current) {
        setIsFetchingParent(false);
      }
    }
  };

  const onProductParentChange = async (value: string): Promise<void> => {
    if (!mounted.current || !product) {
      return;
    }
    const response = await updateProductParent({
      parentProductEntity: value,
      productId: product.id,
      token,
    });
    const djangoMessage = {
      appearance: NotificationAppearance.SUCCESS,
      content: "",
    };
    if (response.result) {
      if (response.product) {
        syncParentFieldsFromProduct(response.product);
      } else {
        setCurrentParentEntity(value);
        setParentEntity(value);
      }
    } else {
      djangoMessage.appearance = NotificationAppearance.ERROR;
    }
    djangoMessage.content = response.message;
    store.dispatch(setDjangoToastOpen(djangoMessage));
  };

  return (
    <div
      data-testid={"product-detail-edit-parent-button"}
      className="tw-flex tw-flex-col tw-min-w-0"
    >
      <label htmlFor="parent-product" className="tw-col-span-full">
        Parent product
      </label>
      <div className="tw-flex tw-items-stretch tw-min-w-0">
        {currentParentEntity && (
          <a
            data-balloon="Go to parent product"
            data-balloon-pos="up-right"
            className="txu-btn txu-compact-very txu-secondary-alt tw-flex tw-items-center !tw-border-r-0 !tw-rounded-r-none tw-shrink-0"
            data-testid={"product-detail-edited-parent-button-go-to-parent"}
            href={`/d/${customerSlug}/product/${product?.parent_id}?autologin=${token}`}
            data-subpage={"true"}
            target={"true"}
          >
            <Icon name="open_in_new" />
          </a>
        )}
        <input
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            setParentEntity(event.target.value)
          }
          value={parentEntity || ""}
          placeholder="Enter parent SKU"
          id="parent-product"
          disabled={isFetchingParent}
          className={`txu-input tw-flex-1 tw-min-w-0 tw-overflow-hidden ${
            currentParentEntity ? "!tw-rounded-none" : "!tw-rounded-r-none"
          }`}
          type="text"
        />

        <Button
          className="!tw-rounded-l-none !tw-rounded-r-none !tw-border-l-0 tw-shrink-0"
          size="sm"
          compact
          loading={isFetchingParent}
          content={
            isFetchParentMode
              ? "Fetch parent"
              : currentParentEntity
              ? "Change parent"
              : "Add parent"
          }
          data-testid={
            "product-detail-edited-parent-button-parent-change-button"
          }
          disabled={
            !product ||
            isFetchingParent ||
            (isFetchParentMode ? false : parentEntity == currentParentEntity)
          }
          onClick={(): Promise<void> | void => {
            if (!mounted.current || !product) {
              return;
            }
            if (isFetchParentMode) {
              return onFetchParentFromInriver();
            }
            return onProductParentChange(trimmedInput);
          }}
          type="button"
        />
        <Button
          data-balloon="Remove parent"
          data-balloon-pos="up-right"
          className="!tw-border-l-0 !tw-rounded-l-none tw-shrink-0"
          size="sm"
          icon
          compact
          data-testid={
            "product-detail-edited-parent-button-parent-remove-button"
          }
          disabled={!currentParentEntity || isFetchingParent}
          onClick={(): Promise<void> => {
            if (!mounted.current || !product) {
              return;
            }
            return onProductParentChange("");
          }}
          type="button"
        >
          <Icon name="close" />
        </Button>
      </div>
    </div>
  );
};
